import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { publishToast, queryKeys } from "@nasam/kernel";
import type { MessagePayload } from "firebase/messaging";
import type { NotificationsRepository } from "../domain";
import {
  currentTokenIfGranted,
  deleteCurrentToken,
  isPushSupported,
  onForegroundMessage,
  requestPermissionAndToken,
} from "./createPushMessaging";

export type PushPermissionState =
  | "unsupported"
  | "default"
  | "denied"
  | "granted";

export interface UsePushRegistrationResult {
  readonly permissionState: PushPermissionState;
  readonly enable: () => Promise<void>;
}

function payloadText(
  payload: MessagePayload,
  isAr: boolean,
): { readonly title: string; readonly body: string | undefined } {
  const notification = payload.notification;
  return {
    title: notification?.title ?? (isAr ? "إشعار جديد" : "New notification"),
    body: notification?.body,
  };
}

export function usePushRegistration(
  repository: NotificationsRepository,
  isAr: boolean,
): UsePushRegistrationResult {
  const queryClient = useQueryClient();
  const [permissionState, setPermissionState] =
    useState<PushPermissionState>("unsupported");
  const registeredToken = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Reading through a call, not the raw variable -- TS narrows a captured
    // `let` to a stale literal across the `await`s below and misses that the
    // cleanup closure flips it, which trips no-unnecessary-condition.
    const isCancelled = () => cancelled;

    async function syncPermissionState() {
      const supported = await isPushSupported();
      if (isCancelled()) return;
      if (!supported) {
        setPermissionState("unsupported");
        return;
      }
      setPermissionState(Notification.permission);

      if (Notification.permission === "granted") {
        const token = await currentTokenIfGranted();
        if (isCancelled()) return;
        if (token !== null) {
          registeredToken.current = token;
          await repository.registerDevice({ token, platform: "WEB" });
        }
      }
    }

    void syncPermissionState();
    return () => {
      cancelled = true;
    };
  }, [repository]);

  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      const { title, body } = payloadText(payload, isAr);
      publishToast({
        kind: "info",
        title,
        ...(body === undefined ? {} : { description: body }),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      });
    });
    return unsubscribe;
  }, [isAr, queryClient]);

  useEffect(() => {
    // Runs only on final unmount (logout tears down the whole authenticated
    // shell) -- an ordinary tab close never reaches this cleanup, which is
    // correct: the token should stay registered server-side so background
    // push can still arrive next time the browser opens.
    return () => {
      const token = registeredToken.current;
      if (token !== null) {
        void repository.unregisterDevice(token);
        void deleteCurrentToken();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = async () => {
    const token = await requestPermissionAndToken();
    // Guard the read -- unsupported browsers (or jsdom) have no Notification
    // global at all, and requestPermissionAndToken already no-ops for them.
    if ("Notification" in window) {
      setPermissionState(Notification.permission);
    }
    if (token !== null) {
      registeredToken.current = token;
      await repository.registerDevice({ token, platform: "WEB" });
    }
  };

  return { permissionState, enable };
}
